import "../sellscreendialogs.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    StatusCheck,
    type StatusCheckResult,
    ToastTypeEnum,
    TouchNumbers,
    useToastCreate,
} from "@onshopfront/components";
import moment from "moment";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../../_Bootstrap/hooks";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { type GiftCardPayment } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import {
    addGiftCardToSale,
    type SaleAddGiftCardOptions,
} from "../../../../Actions/SellScreen/SellScreenProductActions";
import Button from "../../../../Common/Buttons/Button";
import PopupManager from "../../../../Common/Popups/PopupManager";
import { getEnterpriseAuthToken } from "../../../../Data/_Bases/BaseEnterpriseRepository";
import { determineDataSource } from "../../../../Data/DataStores/DataSource";
import { ApplicationEventsFromShopfront } from "../../../../Utilities/Platform/Applications/ApplicationEvents";
import { getApplicationManager } from "../../../../Utilities/Platform/Applications/ApplicationUtilities";
import { getOption } from "../../../../Utilities/Platform/Options/Options";
import { getPolicy } from "../../../../Utilities/Platform/Policies/Policies";
import { getMainHost, getVendorSubdomain } from "../../../../Utilities/Platform/Request";

export const getExistingGiftCards = async (
    giftCardSource: string,
    code: string,
    outlet: string | null,
    connection: string,
    allowPending = false
): Promise<Array<GiftCardPayment>> => {
    if(giftCardSource === "internal") {
        // Load from the local file
        const source = await determineDataSource();
        const giftCards = await source.giftCards.getByCode(code);
        const shareGiftCards = getOption("share_gift_cards");

        let giftCardExists = false;
        let hasPending = false;
        let hasActive = false;
        const validGiftCards = [];
        const activeGiftCards = [];

        for(let i = 0, l = giftCards.length; i < l; i++) {
            // Check to see if the gift card is outside the outlet
            if(!shareGiftCards && outlet) {
                if(giftCards[i].outlet_id !== outlet) {
                    continue;
                }
            }

            if(giftCards[i].status === "cancelled") {
                continue;
            }

            const current = giftCards[i].current_amount;

            // Check to see if there is a gift card with remaining amount on them
            if(current === 0 || isNaN(current)) {
                continue;
            }

            // Check to see if the gift card is expired
            if(moment(giftCards[i].expiry).isBefore(moment())) {
                continue;
            }

            if(giftCards[i].status === "pending") {
                if(!allowPending) {
                    continue;
                }

                hasPending = true;
            } else if(giftCards[i].status === "active") {
                hasActive = true;
                activeGiftCards.push(giftCards[i]);
            }

            // There is a valid gift card
            giftCardExists = true;
            validGiftCards.push(giftCards[i]);
        }

        let giftCardsToUse = validGiftCards;

        if(hasActive && hasPending) {
            giftCardsToUse = activeGiftCards;
        }

        if(!hasActive && giftCardExists && hasPending) {
            PopupManager.warning(`The gift card code (${code}) is used on a parked sale`);
        }

        return giftCardsToUse.map(giftCard => ({
            ...giftCard,
            id             : giftCard.uuid,
            current_amount : giftCard.current_amount,
            original_amount: giftCard.original_amount,
            source         : "internal",
        })).toSorted((a, b) => {
            if(a.status === b.status) {
                return 0;
            }

            if(a.status === "active") {
                return 1;
            }

            if(b.status === "active") {
                return -1;
            }

            if(a.status === "pending") {
                return 1;
            }

            if(b.status === "pending") {
                return -1;
            }

            return 0;
        });
    }

    // Load from a network request
    // Use raw fetch instead of our request object
    const response = await fetch(`${giftCardSource}/${code}`, {
        headers: {
            "x-shopfront-token" : await getEnterpriseAuthToken(connection),
            "x-shopfront-vendor": getVendorSubdomain(),
            "x-shopfront-host"  : getMainHost(),
        },
    });

    if(response.status === 404) {
        return [];
    }

    if(response.status !== 200) {
        throw new Error(`Invalid status returned from gift card source: ${response.status}`);
    }

    const giftCard = await response.json();

    if(giftCard.status === "reserved") {
        return [];
    }

    return [{
        ...giftCard,
        id    : giftCard.code,
        source: connection,
    }];
};

export const RequestCode: FC<SaleAddGiftCardOptions> = (options) => {
    const dispatch = useShopfrontDispatch();
    const createToast = useToastCreate();

    const outlet = useShopfrontSelector(state => state.location.outlet ? state.location.outlet.id : null);
    const saleProducts = useShopfrontSelector(state => state.sellscreen.sale.products);
    const saleCodes = useMemo(
        () => saleProducts.flatMap(product => product.giftCard ? [ product.giftCard.code.toLowerCase() ] : []),
        [ saleProducts ]
    );

    const [ value, setValue ] = useState("");
    const [ source, setSource ] = useState("internal");

    const inputRef = useRef<HTMLInputElement | null>(null);
    const submitRef = useRef({
        canSubmit        : false,
        submitWhenChecked: false,
        checking         : false,
        receivedError    : false,
        ignoreError      : false,
    });

    // Guards against a slower, superseded check (e.g. a stalled network request) overwriting submitRef with
    // stale results after a newer check for a later value has already completed.
    const checkIdRef = useRef(0);

    // StatusCheck only marks its check as pending once its debounce actually fires, so mark it eagerly here on
    // every keystroke - otherwise pressing Add during the debounce window would see a stale `checking: false`
    // from the previous check and incorrectly treat the code as already resolved.
    const handleValueChange = useCallback((newValue: string) => {
        submitRef.current.checking = true;
        submitRef.current.canSubmit = false;
        submitRef.current.receivedError = false;
        submitRef.current.ignoreError = false;
        setValue(newValue);

        // Invalidate any currently running status checks
        checkIdRef.current++;
    }, []);

    useEffect(() => {
        if(options.source === "auto" || options.source === "internal") {
            setSource("internal");
        } else {
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    setSource(sources[i].address);

                    return;
                }
            }

            throw new Error("Unable to find gift card source address");
        }
    }, [ options.source ]);

    const addCode = useCallback(() => {
        if(!value) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Please add a valid code for the gift card",
            });

            return;
        }

        if(value.length > 60) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "The Gift Card code should be 60 characters or less.",
            });

            return;
        }

        if(submitRef.current.submitWhenChecked) {
            return;
        }

        if(submitRef.current.receivedError && !submitRef.current.ignoreError) {
            createToast({
                type   : ToastTypeEnum.Warning,
                message: "We received an error while checking if the gift card is valid, if you'd like to " +
                    "continue anyway, please press the add button again",
            });
            submitRef.current.ignoreError = true;
        } else if(submitRef.current.canSubmit || submitRef.current.ignoreError) {
            dispatch(hideDialog());
            dispatch(addGiftCardToSale({
                ...options,
                code: value,
            }));
        } else if(submitRef.current.checking) {
            submitRef.current.submitWhenChecked = true;
        } else {
            createToast({
                type   : ToastTypeEnum.Error,
                message: `The gift card code "${value}" is already in use`,
            });
        }
    }, [ createToast, dispatch, options, value ]);

    const checkCodeStatus = useCallback(async (code: string): Promise<StatusCheckResult> => {
        const checkId = ++checkIdRef.current;

        // Also set eagerly here (in addition to handleValueChange) since StatusCheck runs this check immediately,
        // bypassing the debounce, on mount - before any keystroke has gone through handleValueChange.
        submitRef.current.checking = true;
        submitRef.current.canSubmit = false;

        let result: StatusCheckResult;
        let pipelineMessage: null | string = null;

        if(!code || code.length > 60) {
            result = null;
        } else if(saleCodes.includes(code.toLowerCase())) {
            submitRef.current.receivedError = false;
            submitRef.current.ignoreError = false;
            result = [ false, "Code is already taken" ];
        } else {
            submitRef.current.receivedError = false;
            submitRef.current.ignoreError = false;

            try {
                const codes = await getExistingGiftCards(source, code, outlet, options.source);
                let exists = codes.length > 0;

                if(getApplicationManager().hasApplications()) {
                    const pipelineResponse = await getApplicationManager().sendPipeline(
                        ApplicationEventsFromShopfront.GIFT_CARD_CODE_CHECK,
                        {
                            code,
                            message: null,
                        },
                        {}
                    );

                    if(pipelineResponse.message) {
                        pipelineMessage = pipelineResponse.message;
                        exists = true;
                    }
                }

                result = exists ? [ false, "Code is already taken" ] : true;
            } catch(err) {
                console.error("Getting gift cards by code failed", err);

                if(checkId === checkIdRef.current) {
                    submitRef.current.receivedError = true;
                }

                result = [
                    null,
                    "Unable to verify this code, please try again",
                    [ "far", "triangle-exclamation" ],
                    "text-orange-500",
                ];
            }
        }

        // A newer check has since started - its state has already superseded this one, so don't touch submitRef.
        if(checkId !== checkIdRef.current) {
            return result;
        }

        if(pipelineMessage) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: pipelineMessage,
            });
        }

        submitRef.current.canSubmit = result === true;
        submitRef.current.checking = false;

        if(submitRef.current.submitWhenChecked) {
            submitRef.current.submitWhenChecked = false;

            // Let addCode() make the final call (submit, show the retry-after-error warning, or reject as a
            // duplicate) using the submitRef state this check just settled, rather than duplicating that logic here.
            if(!submitRef.current.canSubmit) {
                inputRef.current?.select();
            }

            addCode();
        }

        return result;
    }, [ addCode, createToast, options.source, outlet, saleCodes, source ]);

    return (
        <div className="request-code">
            <p>Please enter or scan the gift card&apos;s code</p>
            <StatusCheck
                value={value}
                status={checkCodeStatus}
                position="right-2 top-3"
            >
                {hasIcon => (
                    <TouchNumbers
                        ref={inputRef}
                        mode="string"
                        value={value}
                        onChange={handleValueChange}
                        autoFocus
                        selectOnAutoFocus
                        className={hasIcon ? "pr-8" : undefined}
                        onKeyDown={e => {
                            if(e.key === "Enter") {
                                addCode();

                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                    />
                )}
            </StatusCheck>
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={() => addCode()}
                >
                    Add
                </Button>
            </div>
        </div>
    );
};
